import * as Sentry from '@sentry/react';
import {LEGAL_ROUTES} from '@sollapay/utils';
import {GUEST_BASE_PATH, resolveAuthMode} from '@sollapay/web-core';
import {lazy} from 'react';
import {Navigate, createBrowserRouter} from 'react-router-dom';

import {config} from '@/app';
import {RequireAuth} from '@/features/authentication';
import {AppShellSkeleton, GuestLayout, LegalLayout, MainLayout} from '@/layouts';
import {
  BuyerDetailPage,
  GuestEntryPage,
  GuestTrustAccountsPage,
  TeamMembersPage,
  TrustAccountActivationPage,
  TrustAccountListPage,
  TrustAccountViewPage
} from '@/pages';

import {LegacyTeamRedirect} from './LegacyTeamRedirect';

import type {RouteObject} from 'react-router-dom';

// Legal pages are code-split: they bundle the full legal markdown + react-markdown, which
// authenticated console users almost never open. Loaded on demand behind LegalLayout's Suspense.
const TermsOfUsePage = lazy(() =>
  import('@/pages/TermsOfUsePage').then(m => ({default: m.TermsOfUsePage}))
);
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/PrivacyPolicyPage').then(m => ({default: m.PrivacyPolicyPage}))
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// The account pages guests and members both open; a guest opens them under the `/guest` basename.
const accountRoutes: RouteObject[] = [
  {
    path: '/trust-accounts/:id/activation',
    element: <TrustAccountActivationPage />
  },
  {
    path: '/trust-accounts/:id/buyers/:buyerId',
    element: <BuyerDetailPage />
  },
  {
    path: '/trust-accounts/:id/team',
    element: <LegacyTeamRedirect />
  },
  {
    path: '/trust-accounts/:id/:tab?',
    element: <TrustAccountViewPage />
  }
];

const memberShell: RouteObject = {
  element: <MainLayout />,
  children: [
    {
      path: '/',
      element: <Navigate to="/trust-accounts" replace />
    },
    {
      path: '/trust-accounts',
      element: <TrustAccountListPage />
    },
    ...accountRoutes,
    {
      path: '/team',
      element: <TeamMembersPage />
    },
    {
      path: '*',
      element: <Navigate to="/trust-accounts" replace />
    }
  ]
};

const guestShell: RouteObject = {
  element: <GuestLayout />,
  children: [
    {
      path: '/',
      element: <GuestEntryPage />
    },
    {
      path: '/trust-accounts',
      element: <GuestTrustAccountsPage />
    },
    ...accountRoutes,
    {
      path: '*',
      element: <Navigate to="/trust-accounts" replace />
    }
  ]
};

// Same decision as Auth0Provider: a `/guest` path loads the guest router, which sees paths without that prefix.
const isGuestSession = resolveAuthMode(config.auth0.guestClientId) === 'guest';
const appShell = isGuestSession ? guestShell : memberShell;

export const router = sentryCreateBrowserRouter(
  [
    {
      element: <LegalLayout />,
      children: [
        {
          path: LEGAL_ROUTES.termsOfUse,
          element: <TermsOfUsePage />
        },
        {
          path: LEGAL_ROUTES.privacyPolicy,
          element: <PrivacyPolicyPage />
        }
      ]
    },
    {
      element: <RequireAuth returningLoadingContent={<AppShellSkeleton />} />,
      children: [appShell]
    }
  ],
  {basename: isGuestSession ? GUEST_BASE_PATH : undefined}
);
